"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getVendorTimingsByVendorId } from "@/services/api/vendor/timings";
import { toZonedTime } from "date-fns-tz";
import isEmpty from "lodash/isEmpty";
import isNumber from "lodash/isNumber";
import { getEarliestDeliveryDate, getEarliestDeliveryForToday, VendorTimings, DeliverySlotsData, getEarliestDeliveryDateOnly } from "@/utils/delivery-slots";

// IST timezone constant
const IST_TIMEZONE = "Asia/Kolkata";

// Helper function to get current time in IST
const getNowInIST = (): Date => {
  return toZonedTime(new Date(), IST_TIMEZONE);
};

/**
 * Hook to calculate the earliest possible delivery date for a vendor
 * @param vendorId - The ID of the vendor
 * @param availableDeliverySlots - The delivery slots data from checkout context
 * @param maxPrepHours - (Optional) Max prep hours (in hours) to use if slots are not available
 * @returns Object containing calculated earliest delivery date, vendor timings, and loading state
 */
export function useEarliestDelivery(vendorId, availableDeliverySlots, maxPrepHours) {
  const $ = _c(21);
  let t0;
  let t1;
  if ($[0] !== vendorId) {
    t0 = ["vendor-timings", vendorId];
    t1 = () => getVendorTimingsByVendorId(vendorId?.toString() || "");
    $[0] = vendorId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== availableDeliverySlots || $[4] !== maxPrepHours || $[5] !== vendorId) {
    t2 = !!vendorId && (!isEmpty(availableDeliverySlots) || isNumber(maxPrepHours));
    $[3] = availableDeliverySlots;
    $[4] = maxPrepHours;
    $[5] = vendorId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity,
      gcTime: Infinity,
      select: _temp
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    isLoading,
    data: vendorTimings
  } = useQuery(t3);
  let t4;
  if ($[11] !== availableDeliverySlots || $[12] !== maxPrepHours || $[13] !== vendorTimings) {
    bb0: {
      if (!vendorTimings) {
        t4 = null;
        break bb0;
      }
      const currentDate = getNowInIST();
      if (availableDeliverySlots) {
        const todayResult = getEarliestDeliveryForToday(vendorTimings, availableDeliverySlots, currentDate);
        if (todayResult) {
          t4 = todayResult;
          break bb0;
        }
        t4 = getEarliestDeliveryDate(vendorTimings, availableDeliverySlots, currentDate);
        break bb0;
      }
      const effectiveMaxPrep = maxPrepHours ?? 0;
      const resultDate = getEarliestDeliveryDateOnly(vendorTimings, effectiveMaxPrep, currentDate);
      let t5;
      if ($[15] !== resultDate) {
        t5 = resultDate ? {
          deliveryDate: resultDate,
          deliverySlots: []
        } : null;
        $[15] = resultDate;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      t4 = t5;
    }
    $[11] = availableDeliverySlots;
    $[12] = maxPrepHours;
    $[13] = vendorTimings;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const earliestDeliveryDate = t4;
  let t5;
  if ($[17] !== earliestDeliveryDate || $[18] !== isLoading || $[19] !== vendorTimings) {
    t5 = {
      earliestDeliveryDate,
      vendorTimings,
      isLoading
    };
    $[17] = earliestDeliveryDate;
    $[18] = isLoading;
    $[19] = vendorTimings;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}
function _temp(data) {
  return data.results.length > 0 ? data.results[data.results.length - 1] as VendorTimings : undefined;
}